import {Component, effect, inject, signal} from '@angular/core';
import {DeadlineFilterChange, DeadlineFilterComponent} from '../common/deadline-filter/deadline-filter.component';
import {ItemCounterComponent} from '../common/item-counter/item-counter.component';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatRipple} from '@angular/material/core';
import {BadgeUserData, PersonPickerComponent} from '../common/person-picker/person-picker.component';
import {User} from '../../models/user';
import {MatDialog} from '@angular/material/dialog';
import {CsTableComponent} from './cs-table/cs-table.component';
import {CS, CSCountsResponse} from '../../models/cs';
import {CSService} from '../services/cs.service';
import {ActivatedRoute, Router} from '@angular/router';
import {StatusGroup} from '../common/status-parser.pipe';
import {Location} from '@angular/common';
import {ContactService} from '../services/contact.service';
import {CreateCsV2Component} from './create-cs-v2/create-cs-v2.component';
import {SnackbarService} from '../services/snackbar.service';
import {UserService} from '../services/user.service';
import {MatSelectModule} from '@angular/material/select';
import {MatTooltip} from '@angular/material/tooltip';
import {PageEvent} from '@angular/material/paginator';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import dayjs from 'dayjs';

@Component({
  selector: 'app-customer-service',
  imports: [
    DeadlineFilterComponent,
    ItemCounterComponent,
    MatFormField,
    MatInput,
    MatLabel,
    MatRipple,
    PersonPickerComponent,
    CsTableComponent,
    MatSelectModule,
    MatTooltip,
  ],
  templateUrl: './customer-service.component.html',
  styleUrl: './customer-service.component.scss'
})
export class CustomerServiceComponent {
  statusMap = new Map<string, StatusGroup>([
    ['total', StatusGroup.total.setShowSeparator(true)],
    ['open', StatusGroup.open],
    ['inProgress', StatusGroup.inProgress],
    ['delivered', StatusGroup.delivered],
    ['denied', StatusGroup.denied],
  ]);

  csId?: string | null;
  contactId?: string;

  usersToShowBadges?: User[];
  activatedRoute = inject(ActivatedRoute);
  location = inject(Location);
  contactService = inject(ContactService);
  snackService = inject(SnackbarService);
  userService = inject(UserService);
  router = inject(Router);

  pageSize = signal(10);
  private searchSubject = new Subject<string>();
  private searchValue = '';
  private dateFilter = '';
  private todayFilter = false;
  selectedPeople: string[] = [];

  constructor(public csService: CSService) {
    this.activatedRoute.queryParams.subscribe(params => {
      this.contactId = params['contactId'];
      this.location.go('/atendimentos');
      if (this.contactId) {
        this.contactService.getContactById(this.contactId).then(contact => {
          if (contact?.name && !this.searchValue) {
            this.searchValue = contact.name;
            this.searchSubject.next(contact.name);
          }
        }).catch(() => {});
      }
    });
    this.activatedRoute.paramMap.subscribe((param) => {
      this.csId = param.get('csId') ?? undefined;
    });
    effect(() => {
      // Read signals to track them
      const filters = this.csService.csFilters();
      const csList = this.csService.csList();
      const counts = this.csService.csCounts();

      // Get users from filters or extract from CS list
      if (filters?.users?.length) {
        this.usersToShowBadges = filters.users.map(u => ({ id: u.id, name: u.name, badgeColor: u.badgeColor } as User));
      } else {
        this.usersToShowBadges = this.extractUsersFromCS(csList);
      }

      this.setStatusMap(counts);

      if (this.csId && csList.length) {
        const csId = this.csId;
        this.csId = null;
        const cs = csList.find(c => c.id === csId);
        if (cs) {
          this.openDialog(csId);
        } else {
          this.csService.getCSById(csId).then(csData => {
            if (csData.assigneeUserId && !csData.assigneeUser) {
              csData.assigneeUser = this.userService.users().find(u => u.id === csData.assigneeUserId);
            }
            this.openDialogWithData(csData);
          });
        }
      }
    });

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchValue => {
      this.searchValue = searchValue;
      this.fetchCS();
    });
  }

  fetchCS() {
    this.csService.getCSPaginated({
      search: this.searchValue || undefined,
      users: this.selectedPeople.length > 0 ? this.selectedPeople : undefined,
      date: this.dateFilter || undefined,
      today: this.todayFilter || undefined,
      offset: 0,
      limit: this.pageSize()
    });
  }

  setStatusMap(counts: CSCountsResponse | null) {
    this.statusMap.get('total')?.setCount(this.csService.csPagination()?.total ?? 0);
    this.statusMap.get('open')?.setCount(counts?.open ?? 0);
    this.statusMap.get('inProgress')?.setCount(counts?.inProgress ?? 0);
    this.statusMap.get('delivered')?.setCount(counts?.delivered ?? 0);
    this.statusMap.get('denied')?.setCount(counts?.denied ?? 0);
  }

  private extractUsersFromCS(csList: CS[]): User[] {
    const userMap = new Map<string, User>();
    for (const cs of csList) {
      if (cs.assigneeUser && !userMap.has(cs.assigneeUser.id)) {
        userMap.set(cs.assigneeUser.id, cs.assigneeUser);
      }
    }
    return Array.from(userMap.values());
  }

  readonly dialog = inject(MatDialog);
  usersToFilter: User[] = []
  toBeDeleted: string = '';

  openDialog(csId?: string): void {
    const csData = csId ? this.csService.csList().find((cs) => cs.id === csId) : undefined;
    this.openDialogWithData(csData);
  }

  private openDialogWithData(csData?: CS): void {
    const dialogRef = this.dialog.open(CreateCsV2Component, {
      minWidth: '1199px',
      maxHeight: '823px',
      data: { csData },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result && result.delete) {
        this.toBeDeleted = result.delete;
      }
      if(result && result.new){
        const snack = this.snackService.openSnackBar('success',
          'Atendimento salvo com sucesso!',
          'Clique para vê-lo'
        );
        snack.onAction().subscribe(() => {
          this.router.navigate(['atendimentos', result.new]);
        });
      }
      this.location.go('atendimentos');
      // this.update();
    });

  }

  timestampChange(event: DeadlineFilterChange) {
    this.todayFilter = event.today ?? false;
    if (!this.todayFilter) {
      this.dateFilter = '';
    }
    this.fetchCS();
  }

  timestampDateChange(event?: Date) {
    if (event) {
      this.dateFilter = dayjs(event).format('YYYY-MM-DD');
      this.todayFilter = false;
    } else {
      this.dateFilter = '';
    }
    this.fetchCS();
  }

  newCS() {
    this.openDialog();
  }

  openDetails(csId: string) {
    this.openDialog(csId);
  }

  search(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchSubject.next(filterValue);
  }

  personSelected(event: BadgeUserData[]) {
    this.selectedPeople = event.filter((user) => user.selected).map((user) => user.id);
    this.fetchCS();
  }

  onPageChange(event: PageEvent) {
    this.pageSize.set(event.pageSize);
    this.csService.getCSPaginated({
      search: this.searchValue || undefined,
      users: this.selectedPeople.length > 0 ? this.selectedPeople : undefined,
      date: this.dateFilter || undefined,
      today: this.todayFilter || undefined,
      offset: event.pageIndex * event.pageSize,
      limit: event.pageSize
    });
  }
}
